// FK-aware column ranking helper (F5.2-CB5).
//
// At a `join-on` / `where` dotted-access position (`… JOIN customer c ON c.|`)
// the column that FK-references an *already-bound* relation is the one the
// user almost always wants. The resolvers emit every alias column at a flat
// tier, so the ranker falls back to alphabetical order and the FK column
// rarely lands first. This module computes the set of FK-participating column
// names on a dotted alias so each resolver can keep those at tier 1 and demote
// the rest to tier 2 — making the FK column rank first at the join-on/where
// dotted position.
//
// Pure module — no Monaco / I/O. Reuses the same direction + schema-match
// semantics as `fkJoinSnippet.ts` (the snippet generator that lives at the
// FROM position); this is its column-level sibling.

import type {
  AliasBinding,
  CompletionCandidate,
  FkEdge,
} from '../../../core/engines';
import { computeClauseContext } from '@/features/sql-grammar/document/editorContextClause';
import { schemaMatches } from './fkJoinSnippet';
import {
  scanOptionsFor,
  scanRegion,
  type ScanOptions,
} from '@/features/intellisense/context/slotTextScan';
import {
  detectComparisonPartnerColumn,
  partnerAffinityColumns,
  promoteNameIntersectionPkColumns,
  promotePartnerAffinityColumns,
  type NameIntersectionTable,
} from './onPairingAffinity';

/**
 * Single resolver entry point for join-on / where dotted-access column
 * ranking. Order of precedence (Workstream C ranking signal S3):
 *  1. Comparison-partner affinity — `ON s.store_id = i.▮` promotes exactly
 *     the columns FK-affine to `store_id`. Strongest signal; also the only
 *     one that works for CTE aliases (name-based, no FK metadata needed).
 *  2. Endpoint-pair FK demotion (the original F5.2-CB5 rule) — columns of
 *     the dotted relation participating in an FK edge to another bound
 *     relation stay tier 1.
 *  3. Name-intersection + PK fallback — FK-less joins (MERGE staging):
 *     shared-name columns first, the dotted relation's PK topmost.
 * `binding`/`tables` are omitted on the CTE branch — only rule 1 applies
 * there. No-op outside join-on / where clauses.
 */
export function applyJoinOnColumnRanking(args: {
  readonly out: CompletionCandidate[];
  readonly text: string;
  readonly offset: number;
  readonly statementStart: number;
  readonly sqlEngine: string | undefined;
  readonly bindings: readonly AliasBinding[];
  readonly edges: readonly FkEdge[];
  readonly binding?: AliasBinding;
  readonly tables?: readonly NameIntersectionTable[];
}): void {
  if (!isFkRankingDottedContext(args.text, args.offset, args.statementStart, args.sqlEngine)) {
    return;
  }
  const partner = detectComparisonPartnerColumn(args.text, args.offset);
  const partnerApplied =
    partner !== undefined &&
    promotePartnerAffinityColumns(args.out, partnerAffinityColumns(partner, args.edges));
  if (partnerApplied || args.binding === undefined) return;
  const fkCols = fkParticipatingColumnsForAlias(args.binding, args.bindings, args.edges);
  if (fkCols.size > 0) {
    demoteNonFkColumns(args.out, fkCols);
  } else if (args.tables !== undefined) {
    promoteNameIntersectionPkColumns(args.out, args.binding, args.bindings, args.tables);
  }
}

/**
 * Bump every `kind: 'column'` candidate in `out` whose label is NOT in
 * `fkColumns` from tier 1 down to tier 2, leaving the FK-participating columns
 * at tier 1. Because a join-on / where dotted-access position emits only the
 * dotted alias's columns (the resolvers early-return there), this makes the FK
 * column the unique tier-1 entry → it sorts first.
 *
 * No-op when `fkColumns` is empty (no FK edge to a bound relation) so the
 * default alphabetical order is preserved. Mutates `out` in place; only
 * touches column candidates, never tables / keywords / snippets.
 */
export function demoteNonFkColumns(
  out: CompletionCandidate[],
  fkColumns: ReadonlySet<string>,
): void {
  if (fkColumns.size === 0) return;
  for (let i = 0; i < out.length; i++) {
    const c = out[i]!;
    if (c.kind !== 'column' || c.tier !== 1) continue;
    if (fkColumns.has(c.label.toLowerCase())) continue;
    out[i] = { ...c, tier: 2 };
  }
}

/**
 * True when the dotted-access cursor sits inside a `JOIN … ON` or `WHERE`
 * clause — the two positions where FK-aware column ranking applies. A
 * select-list / FROM / GROUP-BY dotted position returns false so the resolver
 * keeps its default flat-tier emission there.
 *
 * `sqlEngine` is the grammar engine string (`'mysql'`, `'postgres'`, …) — pass
 * `databaseEngineIdToSqlEngine(engineId)`. Returns false for `undefined`
 * (the `'mock'` engine has no grammar) so the mock test engine is unaffected.
 *
 * The `deriveCompletionContext` pipeline forces `positionKind` to
 * `'dotted-access'` whenever a dotted base is present, discarding the
 * enclosing clause; this recovers it by re-walking the same clause state
 * machine the context builder uses for non-dotted positions.
 */
export function isFkRankingDottedContext(
  text: string,
  offset: number,
  statementStart: number,
  sqlEngine: string | undefined,
): boolean {
  if (sqlEngine === undefined) return false;
  const clauseContext = computeClauseContext(text, statementStart, offset, sqlEngine);
  const clause = clauseContext?.clause;
  if (clause === 'join-on' || clause === 'where') return true;
  // The clause machine loses the clause inside a ROUTINE BODY — a T-SQL
  // table-valued function's `RETURNS TABLE AS RETURN ( SELECT … JOIN … ON ▮ )`
  // reports no clause at all, so FK ranking silently switched off exactly
  // where a join is being written. Fall back to the nearest clause keyword
  // before the caret, which is what the clause would have been.
  return nearestClauseKeyword(text, statementStart, offset, sqlEngine) !== null;
}

/** Clause keywords that can OPEN an FK-rankable position. */
const FK_RANKABLE_CLAUSE_HEADS: ReadonlySet<string> = new Set(['ON', 'WHERE']);

/** Every keyword that starts a clause — the scan stops at the first one it
 *  meets walking back, and only an `ON` / `WHERE` counts as a hit. */
const CLAUSE_HEADS: ReadonlySet<string> = new Set([
  'ON', 'WHERE', 'SELECT', 'FROM', 'GROUP', 'ORDER', 'HAVING', 'SET', 'VALUES',
  'INTO', 'USING', 'RETURNING', 'LIMIT', 'OFFSET', 'FETCH', 'WHEN', 'THEN',
  'RETURN', 'BY', 'PARTITION', 'AS',
]);

/** `'ON'` / `'WHERE'` when that is the nearest clause keyword before the
 *  caret, else null. Pure text scan over barewords, comment- and
 *  literal-aware, bounded to the statement. */
function nearestClauseKeyword(
  text: string,
  statementStart: number,
  offset: number,
  sqlEngine: string,
): string | null {
  const words = scanRegion(
    text,
    statementStart,
    offset,
    scanOptionsForSqlEngine(sqlEngine),
  ).tokens;
  for (let i = words.length - 1; i >= 0; i -= 1) {
    const word = words[i]!.word;
    if (!CLAUSE_HEADS.has(word)) continue;
    return FK_RANKABLE_CLAUSE_HEADS.has(word) ? word : null;
  }
  return null;
}

/** The grammar-engine string the resolvers pass → the dialect lexing rules the
 *  region scan needs. */
function scanOptionsForSqlEngine(sqlEngine: string): ScanOptions {
  if (sqlEngine === 'mysql') return scanOptionsFor('mysql');
  if (sqlEngine === 'postgres') return scanOptionsFor('pgsql');
  if (sqlEngine === 'oracle') return scanOptionsFor('oracle');
  if (sqlEngine === 'sqlite') return scanOptionsFor('sqlite');
  return scanOptionsFor('mssql');
}

/**
 * Given the alias the user just dotted (`target`), every alias bound in the
 * surrounding scope (`bindings`), and the snapshot FK edges, return the
 * lower-cased set of `target` relation's columns that participate in an FK
 * edge to/from a DIFFERENT bound relation.
 *
 * "Different bound relation" — the edge's other endpoint must match some
 * binding that is NOT `target` itself (compared by alias). This is what makes
 * a self-referential FK (`employees.manager_id → employees.employee_id`)
 * contribute only when two distinct aliases of that table are bound
 * (`employees e JOIN employees m`), never for a lone alias.
 *
 * Schema matching follows `schemaMatches` ("any" when either side is absent)
 * so dialects without first-class schemas (SQLite, MySQL when database ==
 * schema) still surface FK columns.
 */
export function fkParticipatingColumnsForAlias(
  target: AliasBinding,
  bindings: readonly AliasBinding[],
  edges: readonly FkEdge[],
): ReadonlySet<string> {
  const result = new Set<string>();
  const targetTable = target.relation.toLowerCase();
  const targetSchema = target.schema?.toLowerCase();

  // Other bound relations (excluding the dotted alias itself, matched by alias
  // identity). Keyed by `schema|table` lower-cased; schema may be undefined.
  const others = bindings.filter(
    (b) => b.alias.toLowerCase() !== target.alias.toLowerCase(),
  );
  if (others.length === 0) return result;

  for (const fk of edges) {
    const fromTable = fk.fromTable.toLowerCase();
    const toTable = fk.toTable.toLowerCase();
    const fromSchema = fk.fromSchema?.toLowerCase();
    const toSchema = fk.toSchema?.toLowerCase();

    // Edge's from-side is the dotted alias → its columns are fk.fromColumns,
    // and the to-side must be some other bound relation.
    if (fromTable === targetTable && schemaMatches(fromSchema, targetSchema)) {
      const otherEndBound = others.some(
        (b) =>
          b.relation.toLowerCase() === toTable &&
          schemaMatches(toSchema, b.schema?.toLowerCase()),
      );
      if (otherEndBound) {
        for (const c of fk.fromColumns) result.add(c.toLowerCase());
      }
    }

    // Edge's to-side is the dotted alias → its columns are fk.toColumns, and
    // the from-side must be some other bound relation.
    if (toTable === targetTable && schemaMatches(toSchema, targetSchema)) {
      const otherEndBound = others.some(
        (b) =>
          b.relation.toLowerCase() === fromTable &&
          schemaMatches(fromSchema, b.schema?.toLowerCase()),
      );
      if (otherEndBound) {
        for (const c of fk.toColumns) result.add(c.toLowerCase());
      }
    }
  }

  return result;
}
